import { Directive, ElementRef, afterNextRender, inject } from '@angular/core';

/**
 * Centraliza o `<dialog>` do `vivest-modal` na faixa do iframe que o usuário está vendo.
 *
 * O app roda num iframe que o iframe-resizer estica até a altura do conteúdo: o `<dialog>` do
 * design system (`position: fixed; inset: 0; margin: auto`) fica no meio do iframe inteiro,
 * possivelmente fora da tela. Esta diretiva resolve isso com as variáveis publicadas pelo
 * `ViewportVisivelService`; como o `<dialog>` fica no shadow DOM do componente, sem `part`
 * exposto, a regra é aplicada por uma folha de estilo adicional no shadow root. Fora de iframe, os fallbacks equivalem à
 * viewport do próprio documento e nada muda.
 */
@Directive({
  selector: 'vivest-modal',
  standalone: true,
})
export class VivestModalAreaVisivelDirective {
  private readonly elemento = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  constructor() {
    afterNextRender(() => void this.aplicar());
  }

  private async aplicar(): Promise<void> {
    if (!customElements.get('vivest-modal')) {
      return;
    }
    await (this.elemento as HTMLElement & { updateComplete?: Promise<unknown> }).updateComplete;
    const raiz = this.elemento.shadowRoot;
    const folha = folhaAreaVisivel();
    if (raiz && folha && !raiz.adoptedStyleSheets.includes(folha)) {
      raiz.adoptedStyleSheets = [...raiz.adoptedStyleSheets, folha];
    }
  }
}

// Top e bottom delimitam a faixa visível; com a altura "fit-content" do <dialog> e margin auto,
// o modal fica centralizado nela. O conteúdo rola dentro do modal se não couber na faixa.
const CSS_AREA_VISIVEL = `
  dialog {
    top: var(--viewport-visivel-topo, 0px);
    bottom: calc(100vh - var(--viewport-visivel-topo, 0px) - var(--viewport-visivel-altura, 100vh));
    max-block-size: calc(var(--viewport-visivel-altura, 100vh) - 32px);
  }

  .modal {
    max-block-size: calc(var(--viewport-visivel-altura, 100vh) - 32px);
    overflow-y: auto;
  }
`;

let folhaCompartilhada: CSSStyleSheet | null | undefined;

/** Uma única folha para todos os modais; `null` quando o navegador não suporta folhas construídas. */
function folhaAreaVisivel(): CSSStyleSheet | null {
  if (folhaCompartilhada === undefined) {
    try {
      folhaCompartilhada = new CSSStyleSheet();
      folhaCompartilhada.replaceSync(CSS_AREA_VISIVEL);
    } catch {
      folhaCompartilhada = null;
    }
  }
  return folhaCompartilhada;
}
